// Transforms KULA Lesson Designer JSON into KULA Learn slide-based lesson content.
// Per-type conversion is delegated to the SLIDE_REGISTRY — adding a new slide
// type is a one-line registry edit, not a switch-statement update here.
import type {
  SlideBasedContent,
  Slide,
  SlideData,
  ThemeId,
} from '../components/course-builder/slides/slideTypes';
import { findRegistryByDesignerLabel } from '../components/course-builder/slides/slideRegistry';
import { buildNotes, buildText, wordCount, TEXT_SLIDE_MAX_WORDS } from './designer/slideImporters';

// --- Designer JSON schema ---

// --- Designer JSON contract ---
// User-facing names per CLAUDE.md hierarchy:
//   course → headings (blank dividers) → sections (real content) → slides
// The DB still stores everything as `lessons` with HEADING/TEXT types — that
// translation happens in `transformDesignerJSON` and `useCurriculum`.

export interface DesignerJSON {
  title: string;
  code: string;
  total: number;
  time: string;
  /** Top-level groups. Each heading becomes a HEADING-type divider in the curriculum tree. */
  headings?: DesignerHeading[];
  /** @deprecated Pre-rename name for `headings`. Still accepted for back-compat. */
  sections?: any[];
}

export interface DesignerHeading {
  /** Heading title shown as a divider in the curriculum tree. */
  title: string;
  /** Optional internal/creative name not surfaced to students. */
  name?: string;
  accent?: string;
  /** Sections within this heading. Each becomes a TEXT-type lesson with slides. */
  sections?: DesignerSection[];
  /** @deprecated Pre-rename name for `sections`. Still accepted for back-compat. */
  lessons?: any[];
  /**
   * @deprecated Legacy single-section heading: slides directly under the heading.
   * Equivalent to one section titled the same as the heading.
   */
  slides?: DesignerSlide[];
}

export interface DesignerSection {
  /** Section title shown in the curriculum tree under its heading. */
  title: string;
  /** Optional duration override in minutes — derived from slide count if absent. */
  duration?: number;
  slides: DesignerSlide[];
}

export interface DesignerSlide {
  n: number;
  color: string;
  label: string;
  text: string;
  /** Description / alt text for the image — surfaced as an instructor note. */
  image: string | null;
  options: any | null;
  pairs: any | null;
  note: string | null;
  // --- Optional, structured fields the Designer can emit to avoid prose-sniffing ---
  /** Direct media URL (image src or video src). Preferred over the `image` description when present. */
  imageUrl?: string | null;
  /** SURVEY: explicit multi-select toggle. Falls back to text heuristics if absent. */
  multiple?: boolean | null;
  /** CODE: language id for syntax highlighting. Defaults to 'javascript'. */
  language?: string | null;
  /** DOOR: which activity door builds this slide ('scenario' | 'case-study' | …). */
  door?: string | null;
  /** DOOR: one sentence on what the experience should achieve. */
  goal?: string | null;
  /** DOOR: 'short' | 'long'. */
  length?: string | null;
}

// --- Output types ---

export interface ImportedLesson {
  /** Heading divider this section sits under in the curriculum tree. */
  headingTitle: string;
  /** Optional internal name for the heading (not surfaced to students). */
  headingName: string;
  /** Section title — the row a student clicks to open the slides. */
  title: string;
  content: SlideBasedContent;
  duration: number;
  /**
   * When true, the consumer should create a HEADING-type divider with
   * `headingTitle` before this section. False for second+ sections under the
   * same heading — they share the divider already created.
   */
  emitHeading: boolean;
}

export interface TransformResult {
  lessons: ImportedLesson[];
  title: string;
  code: string;
  totalSlides: number;
  estimatedTime: string;
  warnings: string[];
}

// --- Public API ---

/**
 * Strip a ```kula-json ... ``` fence if present. Returns trimmed raw JSON string.
 */
export function stripJsonFence(raw: string): string {
  const trimmed = raw.trim();
  const fenceMatch = trimmed.match(/^```(?:kula-json|json)?\s*\n([\s\S]*?)\n```$/);
  if (fenceMatch) return fenceMatch[1].trim();
  return trimmed;
}

/**
 * Parse + validate Designer JSON. Throws a friendly Error on malformed input.
 * Accepts either the current `headings[]` shape or the legacy `sections[]` shape.
 */
export function parseDesignerJSON(raw: string): DesignerJSON {
  const stripped = stripJsonFence(raw);
  let parsed: any;
  try {
    parsed = JSON.parse(stripped);
  } catch {
    throw new Error('Invalid JSON. Make sure you copied the entire output from the Lesson Designer.');
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('JSON must be an object.');
  }
  const hasHeadings = Array.isArray(parsed.headings) && parsed.headings.length > 0;
  const hasLegacySections = Array.isArray(parsed.sections) && parsed.sections.length > 0;
  if (!hasHeadings && !hasLegacySections) {
    throw new Error('Designer JSON is missing "headings" (or legacy "sections") or the array is empty.');
  }
  return parsed as DesignerJSON;
}

/**
 * Normalise the legacy pre-rename top-level into the current shape.
 * Old `sections[]` (with `heading`/`name`/`slides` or `lessons`) → new `headings[]`.
 */
function normaliseHeadings(input: DesignerJSON): DesignerHeading[] {
  if (Array.isArray(input.headings) && input.headings.length > 0) return input.headings;
  if (!Array.isArray(input.sections)) return [];
  return input.sections.map((s: any) => {
    const heading: DesignerHeading = {
      title: s.title || s.heading || s.name || 'Untitled Heading',
      name: s.name,
      accent: s.accent,
    };
    if (Array.isArray(s.sections) && s.sections.length > 0) heading.sections = s.sections;
    else if (Array.isArray(s.lessons) && s.lessons.length > 0) heading.sections = s.lessons;
    else if (Array.isArray(s.slides) && s.slides.length > 0) heading.slides = s.slides;
    return heading;
  });
}

export function transformDesignerJSON(
  input: DesignerJSON,
  theme: ThemeId = 'modern-light',
): TransformResult {
  const warnings: string[] = [];
  const lessons: ImportedLesson[] = [];
  let totalSlides = 0;

  for (const heading of normaliseHeadings(input)) {
    const headingTitle = heading.title || heading.name || 'Untitled Heading';
    const headingName = heading.name || heading.title || '';

    // Normalise to a list of sections. Three shapes accepted:
    //   - heading.sections[]: current preferred shape
    //   - heading.lessons[]:  pre-rename Phase 6 shape
    //   - heading.slides[]:   legacy single-section heading (slides directly under heading)
    const designerSections: DesignerSection[] =
      Array.isArray(heading.sections) && heading.sections.length > 0
        ? heading.sections
        : Array.isArray(heading.lessons) && heading.lessons.length > 0
          ? heading.lessons
          : Array.isArray(heading.slides) && heading.slides.length > 0
            ? [{ title: headingTitle, slides: heading.slides }]
            : [];

    if (designerSections.length === 0) {
      warnings.push(`Heading "${headingTitle}" has no slides and was skipped.`);
      continue;
    }

    let isFirstSectionUnderHeading = true;
    for (const ds of designerSections) {
      const dsSlides = Array.isArray(ds.slides) ? ds.slides : [];
      if (dsSlides.length === 0) {
        warnings.push(`Section "${ds.title || headingTitle}" has no slides and was skipped.`);
        continue;
      }

      // One Designer slide can import as several (text past a slide's word
      // cap continues onto the next), so order is assigned after conversion.
      const slides: Slide[] = dsSlides
        .flatMap((slideJson, idx) => convertSlide(slideJson, idx, warnings))
        .map((slide, order) => ({ ...slide, order }));
      if (slides.length === 0) continue;

      totalSlides += slides.length;
      lessons.push({
        headingTitle,
        headingName,
        title: ds.title || headingTitle,
        content: { version: 2, theme, slides },
        duration: typeof ds.duration === 'number' && ds.duration > 0
          ? ds.duration
          : Math.max(1, Math.round(slides.length * 0.75)),
        emitHeading: isFirstSectionUnderHeading,
      });
      isFirstSectionUnderHeading = false;
    }
  }

  return {
    lessons,
    title: input.title || 'Imported Lesson',
    code: input.code || '',
    totalSlides,
    estimatedTime: input.time || '',
    warnings,
  };
}

// --- Slide conversion ---

function convertSlide(ds: DesignerSlide, order: number, warnings: string[]): Slide[] {
  const baseNotes = buildNotes(ds);

  const entry = findRegistryByDesignerLabel(ds.label || '');
  let produced: SlideData[];
  if (entry) {
    const cap = entry.designer.maxWords;
    const out = entry.designer.importer(ds, warnings, { maxWords: cap });
    produced = Array.isArray(out) ? out : [out];
    // The registry's TEXT LIMITS are prompt-side guidance; this is the
    // enforcement half. Types that paginate (cover, title, text, image+text)
    // already moved the excess onto continuation slides, so only warn when
    // the text still sits on one slide. Advisory (warn, still import).
    const floor = entry.designer.minWords;
    if ((cap || floor) && typeof ds.text === 'string') {
      const words = wordCount(ds.text);
      if (cap && words > cap && produced.length === 1) {
        warnings.push(
          `Slide ${ds.n ?? order + 1} (${entry.designer.label}): ${words} words — over the ~${cap}-word limit. Trim before publishing.`,
        );
      } else if (floor && words < floor) {
        // The bare-heading failure mode: a content slide whose whole text is
        // a title. maxWords catches too-long; this catches placeholder-short.
        warnings.push(
          `Slide ${ds.n ?? order + 1} (${entry.designer.label}): only ${words} word${words === 1 ? '' : 's'} — looks like a heading without a slide. Add the content before publishing.`,
        );
      }
    }
  } else {
    warnings.push(`Slide ${ds.n ?? order + 1}: Unknown type "${ds.label}", imported as Text.`);
    const out = buildText(ds, warnings, { maxWords: TEXT_SLIDE_MAX_WORDS });
    produced = Array.isArray(out) ? out : [out];
  }

  // Build notes stay with the first slide — the one the designer briefed.
  return produced.map((data, i) => {
    const slide: Slide = { id: crypto.randomUUID(), order, data };
    if (i === 0 && baseNotes) slide.notes = baseNotes;
    return slide;
  });
}


// --- Canvas → DesignerJSON adapter ---

import type { CanvasSection } from './llm-tools/types';

/**
 * Adapt the Designer's accumulated canvas state (`propose_sections` tool
 * calls) into the DesignerJSON shape `transformDesignerJSON` consumes. The
 * canvas is the single source of truth for the carry-into-Builder import —
 * what the artefact pane shows is literally what gets created. Consecutive
 * sections sharing a `heading` are grouped under one divider; sections
 * without one fall under a single default chapter.
 */
export function canvasSectionsToDesignerJSON(
  sections: CanvasSection[],
  meta: { title: string },
): DesignerJSON {
  const fallbackHeading = meta.title?.trim() || 'New chapter';
  const headings: DesignerHeading[] = [];
  for (const sec of sections) {
    const headingTitle = sec.heading?.trim() || fallbackHeading;
    let target = headings[headings.length - 1];
    if (!target || target.title !== headingTitle) {
      target = { title: headingTitle, sections: [] };
      headings.push(target);
    }
    target.sections!.push({
      title: sec.name,
      ...(typeof sec.duration === 'number' && sec.duration > 0
        ? { duration: sec.duration }
        : {}),
      slides: sec.slides.map((sl, i) => ({
        n: i + 1,
        color: sl.color || '#6c63ff',
        label: sl.type,
        text: sl.text,
        image: sl.image ?? null,
        options: sl.options ?? null,
        pairs: sl.pairs ?? null,
        note: sl.note ?? null,
        ...(sl.imageUrl != null ? { imageUrl: sl.imageUrl } : {}),
        ...(sl.multiple != null ? { multiple: sl.multiple } : {}),
        ...(sl.language != null ? { language: sl.language } : {}),
        ...(sl.door != null ? { door: sl.door } : {}),
        ...(sl.goal != null ? { goal: sl.goal } : {}),
        ...(sl.length != null ? { length: sl.length } : {}),
      })),
    });
  }
  const total = sections.reduce((n, s) => n + s.slides.length, 0);
  return {
    title: meta.title || 'Untitled',
    code: '',
    total,
    time: `~${Math.max(1, Math.round(total * 0.8))} min`,
    headings,
  };
}

// --- Placement insertion (Carry doorway "After <chapter>" choice) ---

export interface CurriculumRow {
  id: string;
  isHeading: boolean;
}

/**
 * Compute the full lesson-id order that places freshly-imported lessons at
 * the end of an existing chapter instead of the end of the course. `existing`
 * is the pre-import flat list in display order (the import appended `newIds`
 * after it); the result is the id list to hand to `reorderLessons`. The
 * insertion point is just before the next HEADING after the anchor — i.e.
 * after the anchor chapter's last lesson. Returns null when the anchor
 * heading isn't found, in which case the caller skips the reorder and the
 * append stands.
 */
export function orderWithChapterInsertion(
  existing: CurriculumRow[],
  newIds: string[],
  afterHeadingId: string,
): string[] | null {
  const anchorIdx = existing.findIndex((r) => r.id === afterHeadingId && r.isHeading);
  if (anchorIdx === -1) return null;
  let insertAt = existing.length;
  for (let i = anchorIdx + 1; i < existing.length; i++) {
    if (existing[i].isHeading) {
      insertAt = i;
      break;
    }
  }
  return [
    ...existing.slice(0, insertAt).map((r) => r.id),
    ...newIds,
    ...existing.slice(insertAt).map((r) => r.id),
  ];
}

/**
 * True when any canvas slide carries production-fidelity fields. Used to
 * decide whether the canvas (new single-stream sessions) or a persisted
 * `latestJson` (legacy update_brief sessions) is the richer import source.
 */
export function canvasHasProductionFidelity(sections: CanvasSection[]): boolean {
  return sections.some((sec) =>
    sec.slides.some(
      (sl) =>
        sl.note != null ||
        sl.options != null ||
        sl.pairs != null ||
        sl.image != null ||
        sl.imageUrl != null ||
        sl.multiple != null ||
        sl.language != null,
    ),
  );
}
